import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';

import css from './SingleSelectFilter.module.css';

const ChevronIcon = ({ open }) => (
  <svg
    className={classNames(css.chevron, { [css.chevronOpen]: open })}
    width="12"
    height="8"
    viewBox="0 0 12 8"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M1 1.5l5 5 5-5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * SingleSelectFilter — FILTERS-03
 *
 * Single-value dropdown with the standard sidebar visual system.
 * The panel shows a reset row at the top followed by the predefined
 * options. Click applies + closes.
 *
 * @param {Object} props
 * @param {string} props.label - Header shown above the trigger
 * @param {string} props.placeholder - Trigger text when nothing is selected
 * @param {Array<{value: string, label: string}>} props.options
 * @param {Array<string>} props.queryParamNames - e.g. ["pub_tipo_operacion"]
 * @param {Object} props.initialValues - { [queryParam]: value }
 * @param {Function} props.onSubmit - receives { [queryParam]: value | null }
 */
const SingleSelectFilter = props => {
  const { label, placeholder = 'Todos', options, queryParamNames, initialValues, onSubmit } = props;
  const queryParam = queryParamNames?.[0];
  const initialRaw = queryParam ? initialValues?.[queryParam] : null;

  const initialValue = useMemo(() => {
    if (initialRaw == null || initialRaw === '') return null;
    return String(initialRaw);
  }, [initialRaw]);

  const [selectedValue, setSelectedValue] = useState(initialValue);
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  // Sync from external URL changes (clear all, other filters, etc.).
  useEffect(() => {
    setSelectedValue(initialValue);
  }, [initialValue]);

  const close = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleDocClick = e => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) close();
    };
    const handleKey = e => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', handleDocClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleDocClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [isOpen, close]);

  const apply = value => {
    setSelectedValue(value);
    onSubmit({ [queryParam]: value });
  };
  const reset = () => {
    setSelectedValue(null);
    onSubmit({ [queryParam]: null });
  };

  const handleSelect = value => {
    apply(value);
    close();
  };
  const handleReset = () => {
    reset();
    close();
  };

  const selectedOption = options.find(o => o.value === selectedValue) || null;
  const triggerLabel = selectedOption ? selectedOption.label : placeholder;
  const isPlaceholder = selectedOption == null;

  return (
    <div className={css.root}>
      <div className={css.filterHeader}>
        {/* FILTER-ACTIVE-INDICATOR: título resaltado cuando hay selección. */}
        <p className={classNames(css.label, { [css.labelActive]: !isPlaceholder })}>{label}</p>
      </div>
      <div className={css.wrapper} ref={wrapperRef}>
        <button
          type="button"
          className={classNames(css.trigger, {
            [css.triggerOpen]: isOpen,
            [css.triggerPlaceholder]: isPlaceholder,
          })}
          onClick={() => setIsOpen(prev => !prev)}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          <span>{triggerLabel}</span>
          <ChevronIcon open={isOpen} />
        </button>

        {isOpen ? (
          <div className={css.panel} role="listbox">
            <ul className={css.list}>
              <li
                className={classNames(css.option, css.optionReset)}
                role="option"
                aria-selected={selectedValue == null}
                tabIndex={0}
                onClick={handleReset}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleReset();
                  }
                }}
              >
                {placeholder}
              </li>
              {options.map(opt => (
                <li
                  key={opt.value}
                  className={classNames(css.option, {
                    [css.optionActive]: selectedValue === opt.value,
                  })}
                  role="option"
                  aria-selected={selectedValue === opt.value}
                  tabIndex={0}
                  onClick={() => handleSelect(opt.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleSelect(opt.value);
                    }
                  }}
                >
                  {opt.label}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default SingleSelectFilter;
